import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import {
  getOptions,
  mineSubsidenceRequired,
  setState,
  mandatoryWindHailDeductibleByCounty,
  mandatoryWindHailDeductibleByZip,
  hurricaneDeductibleStates,
  windHailDeductibleCanVaryByCounty,
  noWindHailDeductibleStates,
  coverageDNotOffered,
  ibscDescriptionByState,
  restrictedBLimitsStates,
  restrictedCLimitsStates,
  restrictedDLimitsStates,
  unlimitedDLimitsStates,
  UNLIMITED_D_VALUE,
  MAX_COVERAGE_A_TO_RCE_RATIO,
  policyType
} from '@ourbranch/lookups';
import { Form, useFormikContext } from 'formik';

import { currencyFormatter } from 'core/helpers/formatters';
import useSession from 'core/hooks/use-session';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { NotificationCard } from 'core/components/notification-card';
import { useCurrentState } from 'common/hooks/useCurrentState';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import Section from 'core/components/section';
import CollapsibleCard from 'core/components/collapsible-card';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { numberThousandsFormatter } from 'core/helpers/formatters';
import { Loading } from 'core/components/loading';
import { appConfig } from 'brand-config';
import { CoverageLimitOverride, isCoverageLimitLockedForUser } from 'common/components/coverage-limit-override';
import OtherCoverages from './other-coverages';
import AdditionalCoverages from './additional-coverages';
import WindHailFlow from './wind-hail-flow';
import { getWindHailTooltip } from './wind-hail-tooltip';
import useStyles from '../coverages.styles';

const id = 'homeCoverage';

const buildOptions = (value, letter, state, numFamiliesInDwelling) => {
  let min = 0;
  let max = 100;
  switch (letter) {
    case 'B':
      min = 0;
      if (restrictedBLimitsStates.includes(state)) {
        max = 10;
      } else {
        max = 100;
      }
      break;
    case 'C':
      min = 0;
      if (state === 'MD') {
        min = 40;
        max = 75;
      } else if (restrictedCLimitsStates.includes(state)) {
        if (!numFamiliesInDwelling || numFamiliesInDwelling <= 2) {
          max = 60;
        } else if (numFamiliesInDwelling === 3) {
          max = 40;
        } else {
          max = 35;
        }
      } else {
        max = 100;
      }
      break;
    case 'D':
      if (restrictedDLimitsStates.includes(state)) {
        // these states fix ALE (CovD) at 10% of CovA
        min = 10;
        max = 10;
      } else {
        min = 0;
        max = 40;
      }
      break;
  }
  const array = [];
  for (let num = min; num <= max; num += 5) {
    array.push({ id: num, value: value ? `${num}% ($${((num / 100) * value).toLocaleString('en-US')})` : `${num}%` });
  }

  if (letter === 'D' && unlimitedDLimitsStates.includes(state)) {
    array.push({ id: UNLIMITED_D_VALUE, value: 'Unlimited for up to 24 months' });
  }

  return array;
};

const buildingStructureOptions = (value, state) => {
  const options = getOptions('coverageIBSC', state);
  const formattedOptions = options.map((option) => {
    return {
      id: option.id,
      value: value ? `${option.value} ($${((option.id / 100) * value).toLocaleString('en-US')})` : option.value
    };
  });

  return formattedOptions;
};

function HomeCoverage({ disabled, fromPolicy }) {
  const classes = useStyles();
  const { setFieldValue, values, errors } = useFormikContext();
  const { homeCoverage, home } = values;
  const state = useCurrentState(values, { policyType: policyType.Home });
  const { canEditCoverageA, canOverrideCoverageLimit } = useSession();

  // #35617: home Coverage A limit override. Home derives B/C/D as percent-of-A, so locking A is enough.
  const coverageLimitOverrideEnabled = Boolean(appConfig?.featureFlags?.coverageLimitOverride);
  const homeCoverageLimitLockedForUser = isCoverageLimitLockedForUser({
    coverages: homeCoverage,
    canOverrideCoverageLimit,
    coverageLimitOverrideEnabled
  });

  // Wait for state to load before rendering to prevent incorrect showDeductibleWindHail calculation
  if (!state) {
    return (
      <Section title="Home Policy Coverage" type="SubSection">
        <Loading noBackground />
      </Section>
    );
  }

  const county = home.county?.toUpperCase();
  const enforceMS = !!(mineSubsidenceRequired[state] && mineSubsidenceRequired[state][county]);
  const showDeductibleWindHail =
    (!windHailDeductibleCanVaryByCounty[state] ||
      (windHailDeductibleCanVaryByCounty[state] && windHailDeductibleCanVaryByCounty[state][county])) &&
    !noWindHailDeductibleStates[state];

  const showCoverageD = !coverageDNotOffered[state];

  const { coverageA, windHailExclusion } = homeCoverage;
  const minimumDeductibleValue = homeCoverage.minimumDeductibleValue || 100;
  const minimumWindHailDeductibleValue = homeCoverage.minimumWindHailDeductibleValue || 100;
  const minimumHurricaneDeductibleValue = homeCoverage.minimumHurricaneDeductibleValue || 100;

  const windHailTooltip = getWindHailTooltip({ state, windHailExclusion: homeCoverage?.windHailExclusion });

  // for github issue 17316, removing dollar-amount deductibles under $2000
  // outside of some states

  const windHailOptions = getOptions('deductibleWindHail', state).filter(
    ({ id }) => fromPolicy || ['KY', 'ME', 'NH'].includes(state) || +id < 1 || +id >= minimumWindHailDeductibleValue
  );
  const hurricaneOptions = getOptions('deductibleHurricane', state).filter(
    ({ id }) => fromPolicy || ['KY', 'ME', 'NH'].includes(state) || +id < 1 || +id >= minimumHurricaneDeductibleValue
  );
  const allOtherOptions = getOptions('deductibleAllOther', state).filter(
    ({ id }) => fromPolicy || ['KY', 'ME', 'NH'].includes(state) || +id < 1 || +id >= minimumDeductibleValue
  );

  setState(state);

  useEffect(() => {
    if (enforceMS) {
      setFieldValue('homeCoverage.coverageMS', true);
    }
  }, [enforceMS, homeCoverage, setFieldValue]);

  const formatPercentageOptionValue = useCallback(
    (o) => {
      const option = { ...o, value: o.value };
      const value = option.id * coverageA;
      option.value = `${option.value} ($${numberThousandsFormatter(value)})`;
      return option;
    },
    [coverageA]
  );

  const mapDeductible = useCallback(
    (option) => (option.id < 1 ? formatPercentageOptionValue(option) : option),
    [formatPercentageOptionValue]
  );

  const filterLowerThanMinimumDeductible = useCallback(
    (option) => {
      const value = option.id > 1 ? option.id : option.id * coverageA;

      return value >= minimumDeductibleValue;
    },
    [coverageA, minimumDeductibleValue]
  );

  const filterLowerThanMinimumOrWindHailDeductible = useCallback(
    (option) => {
      const value = option.id > 1 ? option.id : option.id * coverageA;
      if (
        mandatoryWindHailDeductibleByZip?.[state]?.[home.homeLocation.zip] ||
        mandatoryWindHailDeductibleByCounty?.[state]?.[home.county]
      ) {
        const MDmin =
          mandatoryWindHailDeductibleByZip?.[state]?.[home.homeLocation.zip] ||
          mandatoryWindHailDeductibleByCounty?.[state]?.[home.county];
        if (MDmin < 1) {
          return (
            value >= minimumDeductibleValue &&
            value >= minimumWindHailDeductibleValue &&
            option.id < 1 &&
            option.id >= MDmin
          );
        }
        return value >= minimumDeductibleValue && value >= minimumWindHailDeductibleValue && value >= MDmin;
      }
      return value >= minimumDeductibleValue && value >= minimumWindHailDeductibleValue;
    },
    [coverageA, minimumDeductibleValue, minimumWindHailDeductibleValue, state, home.county, home.homeLocation.zip]
  );

  const filterLowerThanMinimumOrHurricaneDeductible = useCallback(
    (option) => {
      const value = option.id > 1 ? option.id : option.id * coverageA;
      return value >= minimumDeductibleValue && value >= minimumHurricaneDeductibleValue;
    },
    [coverageA, minimumDeductibleValue, minimumHurricaneDeductibleValue]
  );

  const allOtherOptionsFormatted = useMemo(
    () => allOtherOptions.filter(filterLowerThanMinimumDeductible).map(mapDeductible),
    [allOtherOptions, filterLowerThanMinimumDeductible, mapDeductible]
  );

  const windHailOptionsFormatted = useMemo(
    () => windHailOptions.filter(filterLowerThanMinimumOrWindHailDeductible).map(mapDeductible),
    [windHailOptions, filterLowerThanMinimumOrWindHailDeductible, mapDeductible]
  );

  const hurricaneOptionsFormatted = useMemo(
    () => hurricaneOptions.filter(filterLowerThanMinimumOrHurricaneDeductible).map(mapDeductible),
    [hurricaneOptions, mapDeductible, filterLowerThanMinimumOrHurricaneDeductible]
  );

  const showDecreaseDwellingLimit = home.replacementCostEstimate.total < homeCoverage.coverageA;

  const { inOfferEditRoute, inPolicyRoute } = useRouteHelper();

  const rce = home.replacementCostEstimate.total;
  const rce120Percent = rce * MAX_COVERAGE_A_TO_RCE_RATIO;
  const rceFormatted = currencyFormatter(rce, { removeDecimal: true });

  // Determine state-specific max dwelling limit.
  // Mirrors home.validation-schema.js — keep both in sync.
  const stateMaxLimit = state === 'TX' || state === 'PA' ? 950000 : 1500000;
  const actualMax = Math.min(rce120Percent, stateMaxLimit);
  const maxFormatted = currencyFormatter(actualMax, { removeDecimal: true });

  // When RCE is over the state max, the 50012 rejection banner already explains
  // the situation; suppress the helper text since no Cov A value is valid. The
  // rejection self-cures (GitHub #34385) once corrected details bring the
  // recalculated RCE (and Cov A) back within the state max and the offer updates.
  const coverageAAdjustmentRange =
    actualMax < rce120Percent
      ? `You may adjust this from ${rceFormatted} (100% RCE) to ${maxFormatted} (maximum Cov A in this state)`
      : `You may adjust this from ${rceFormatted} (100% RCE) to ${maxFormatted} (120% RCE).`;
  const coverageADescription = inOfferEditRoute && rce <= stateMaxLimit ? coverageAAdjustmentRange : null;
  const currentCovAFormatted = homeCoverage.coverageA
    ? `${currencyFormatter(homeCoverage.coverageA, { removeDecimal: true })}`
    : '';

  return (
    <Section title="Home Policy Coverage" type="SubSection">
      <div className={classes.container}>
        <Form disabled={disabled} key={state}>
          <Grid container justifyContent="flex-start" alignItems="center" spacing={4} className={classes.containerDark}>
            {/* dwelling coverages */}
            <Grid item xs={12}>
              <Label key="lblTop" type="formSubTitle">
                Dwelling coverages
              </Label>
            </Grid>
            <Grid container item className={inOfferEditRoute && classes.spacingOnBottomForDescription}>
              <Grid item xs={6}>
                {inOfferEditRoute || (inPolicyRoute && canEditCoverageA) ? (
                  // CAN EDIT COVERAGE A
                  <LabelTooltip
                    label="Dwelling Limit"
                    tooltip={{
                      label: 'More Info',
                      onHoverText: tooltipHoverTexts.dwellingLimit
                    }}
                  >
                    <Field
                      id={`${id}.coverageA`}
                      name={`${id}.coverageA`}
                      type="numeric"
                      mode="dark"
                      description={!errors.homeCoverage?.coverageA && coverageADescription} // show description only if there is no error, otherwise the two texts will be overlapping
                      format={{ thousandSeparator: true, prefix: '$' }}
                      fast={false}
                      disabled={homeCoverageLimitLockedForUser}
                    />
                  </LabelTooltip>
                ) : (
                  // CANNOT EDIT COVERAGE A
                  <LabelTooltip
                    label="Dwelling Limit"
                    tooltip={{
                      label: 'More Info',
                      onHoverText: tooltipHoverTexts.dwellingLimit
                    }}
                  >
                    <Field
                      id={`${id}.coverageA`}
                      name={`${id}.coverageA`}
                      type="value"
                      mode="dark"
                      fast={false}
                      value={currentCovAFormatted}
                    />
                  </LabelTooltip>
                )}
              </Grid>
              <Grid item xs={6}>
                <LabelTooltip
                  label="Increased Building Structure"
                  tooltip={{
                    label: 'More Info',
                    onHoverText: ibscDescriptionByState[state] || tooltipHoverTexts.increasedBuildingStructure
                  }}
                >
                  <Field
                    id={`${id}.coverageIBSC`}
                    name={`${id}.coverageIBSC`}
                    type="select"
                    mode="dark"
                    options={buildingStructureOptions(homeCoverage.coverageA, state)}
                    fast={false} // need this so react updates the options when coverageA changes
                  />
                </LabelTooltip>
              </Grid>
            </Grid>
            {homeCoverageLimitLockedForUser && (
              <Label type="infoLabel" className={classes.coverageLimitLockWarning}>
                The Coverage A limit is locked. Please reach out to underwriting to make changes.
              </Label>
            )}
            <CoverageLimitOverride
              fieldName={id}
              coverageLabel="Coverage A"
              coverages={homeCoverage}
              disabled={disabled}
            />
            {fromPolicy && showDecreaseDwellingLimit && (
              <NotificationCard type="quaternary">
                To decrease the Dwelling Limit to this home's Replacement Cost Estimate, please contact support
              </NotificationCard>
            )}
            <Grid item xs={4}>
              <LabelTooltip
                label="Other Structures limit"
                tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.otherStructuresLimit }}
              >
                <Field
                  id={`${id}.coverageBPctOfA`}
                  name={`${id}.coverageBPctOfA`}
                  type="select"
                  mode="dark"
                  options={buildOptions(homeCoverage.coverageA, 'B', state)}
                  fast={false} // need this so react updates the options when coverageA changes
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={4}>
              <LabelTooltip
                label="Personal Property limit"
                tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.personalPropertyLimit }}
              >
                <Field
                  id={`${id}.coverageCPctOfA`}
                  name={`${id}.coverageCPctOfA`}
                  type="select"
                  mode="dark"
                  options={buildOptions(homeCoverage.coverageA, 'C', state, home.numFamiliesInDwelling)}
                  fast={false} // need this so react updates the options when coverageA changes
                />
              </LabelTooltip>
            </Grid>
            {showCoverageD && (
              <Grid item xs={4}>
                <LabelTooltip
                  label="Additional Living Expense"
                  tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.additionalLivingExpense }}
                >
                  <Field
                    id={`${id}.coverageDPctOfA`}
                    name={`${id}.coverageDPctOfA`}
                    type="select"
                    mode="dark"
                    options={buildOptions(homeCoverage.coverageA, 'D', state)}
                    fast={false} // need this so react updates the options when coverageA changes
                  />
                </LabelTooltip>
              </Grid>
            )}

            {/* deductibles */}
            <Grid container item spacing={2} className={classes.coverageContainer}>
              <Grid item xs={12}>
                <Label key="lblTop" type="formSubTitle" className={classes.cardSubSectionDivider}>
                  Deductible
                </Label>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs>
                  <LabelTooltip
                    label="Deductible"
                    tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.deductibleAllOther }}
                  >
                    <Field
                      id={`${id}.deductibleAllOther`}
                      name={`${id}.deductibleAllOther`}
                      type="select"
                      mode="dark"
                      options={allOtherOptionsFormatted}
                    />
                  </LabelTooltip>
                </Grid>
                {showDeductibleWindHail && (
                  <Grid item xs>
                    <LabelTooltip
                      label="Wind & Hail deductible"
                      tooltip={{ label: 'More Info', onHoverText: windHailTooltip }}
                    >
                      <Field
                        id={`${id}.deductibleWindHail`}
                        name={`${id}.deductibleWindHail`}
                        type="select"
                        mode="dark"
                        options={windHailOptionsFormatted}
                        disabled={windHailExclusion}
                        fast={false}
                      />
                    </LabelTooltip>
                  </Grid>
                )}
                {hurricaneDeductibleStates[state] && (
                  <Field
                    id={`${id}.deductibleHurricane`}
                    name={`${id}.deductibleHurricane`}
                    type="select"
                    label={state === 'LA' ? 'Named Storm deductible' : 'Hurricane deductible'}
                    mode="dark"
                    options={hurricaneOptionsFormatted}
                    xs
                  />
                )}
              </Grid>
              {windHailExclusion && <WindHailFlow fromPolicy={fromPolicy} />}
            </Grid>

            {/* Other Coverages */}
            <OtherCoverages
              id={id}
              enforceMS={enforceMS}
              state={state}
              coverageA={homeCoverage.coverageA}
              fromPolicy={fromPolicy}
            />

            {/* Additional Coverages */}
            <CollapsibleCard
              initialOpen={false}
              enclosedCard
              content={<Label type="formSubTitle">Additional coverages</Label>}
              invertOpenClose
              closedContentRight={<Label type="infoCardText">See more coverages</Label>}
              openContentRight={<Label type="infoCardText">See less coverages</Label>}
              type={'secondary'}
              className={classes.outlineContainer}
            >
              <AdditionalCoverages id={id} state={state} />
            </CollapsibleCard>
          </Grid>
        </Form>
      </div>
    </Section>
  );
}

HomeCoverage.propTypes = {
  disabled: PropTypes.bool,
  fromPolicy: PropTypes.bool
};

HomeCoverage.defaultProps = {
  disabled: false,
  fromPolicy: false
};

export default HomeCoverage;
